import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ModalEventEnum } from 'src/types';
import { TrackingListService } from 'src/services/tracking-list.service';
import { CodebookService } from 'src/services/codebook.service';
import { map, startWith } from 'rxjs/operators';
import { autoSelect, sortByFirstLetter } from 'src/app/utils/autoSelect';
import { LocalStorageService } from 'src/app/local-storage.service';
import { ServiceProvidersService } from 'src/services/service-providers.service';

interface TrackingData {
  trackingData: any;
  action: ModalEventEnum.CANCEL;
  subjectId: any;
}

enum AutocompleteFilterEnum {
  ACTIVITY = 'activity',
  PROVIDED_SERVICE = 'providedService',
}

@Component({
  selector: 'app-service-provider-manage-tracking',
  templateUrl: './service-provider-manage-tracking.component.html',
  styleUrls: ['./service-provider-manage-tracking.component.scss']
})
export class ServiceProviderManageTrackingComponent implements OnInit {
  action: string;
  data: any;
  isEditing = false;
  isNew = false;
  trackingDocumentForm: FormGroup;
  item: any;
  providedServices = [];
  filteredProvidedServices: Observable<any[]>;
  activities = [];
  filteredActivities: Observable<any[]>;
  maxDate: Date = new Date(2999, 11, 31);
  isActivitySelected: boolean = false;
  loggedIn: any;
  serviceProviderId;

  constructor(
    private dialogRef: MatDialogRef<any>,
    @Optional() @Inject(MAT_DIALOG_DATA) public tracking: TrackingData,
    private formBuilder: FormBuilder,
    private trackingListService: TrackingListService,
    private codebookService: CodebookService,
    private localStorageService: LocalStorageService,
    private serviceProviderService: ServiceProvidersService
  ) { 
    this.loggedIn = JSON.parse(this.localStorageService.get('loggedUser'));
    this.serviceProviderId = this.loggedIn?.serviceProviderId;
  }

  ngOnInit(): void {
    this.data = this.tracking.trackingData;
    this.action = this.tracking.action;

    this.trackingDocumentForm = this.formBuilder.group({
      subjectId: this.data.subjectId,
      providedService: ['', [Validators.required]],
      activity: ['', [Validators.required]],
      note: ['', [Validators.required]],
      date: ['', Validators.required],
      dateOfModification: [null],
    });

    if (this.action === ModalEventEnum.EDIT) {
      this.toggleEdit();
      this.isEditing = true;
    }

    if (this.action === ModalEventEnum.NEW) {
      this.toggleNew();
      this.isNew = true;
    }

    if (this.isNew || this.isEditing) {
      this.serviceProviderService.findProvidedServicesByServiceProviderId(this.serviceProviderId).subscribe(result => {
        this.providedServices = result;
        this.filteredProvidedServices = this.trackingDocumentForm.controls.providedService.valueChanges.pipe(
          startWith(''),
          map(value => (typeof value === 'string' ? value : value?.title)),
          map(title => (title ? this._filter(title, AutocompleteFilterEnum.PROVIDED_SERVICE) : this.providedServices.slice()))
        )
      });
    }
  }

  toggleEdit() {
    this.isEditing = !this.isEditing;

    this.trackingDocumentForm.patchValue({
      providedService: this.data.providedService,
      activity: this.data.activity,
      note: this.data.description,
      date: this.data.date,
      dateOfModification: this.data.dateOfModification,
    });
  }

  toggleNew() {
    this.isNew = true;
    this.trackingDocumentForm.patchValue({});
  }

  handleDelete() {
    this.action = 'delete';
    this.trackingListService.removeItem(this.tracking.trackingData.id).subscribe(
      (res: any) => {
        this.dialogRef.close({ event: ModalEventEnum.DELETE });
      },
      (err: string) => {
        console.log('Error occured' + err);
      }
    );
  }

  compareActivityObjects(object1: any, object2: any) {
    return object1 && object2 && object1.id === object2.id && object1.title === object2.title;
  }

  manageTrackingDocument(trackingDocumentForm: FormGroup) {
    if (this.action === 'view' || this.action === 'new' || this.action === 'edit') {
      this.item = {
        id: this.tracking.trackingData.id,
        serviceProviderId: this.loggedIn?.serviceProviderId,
        serviceProviderSubjectId: Number(this.tracking.subjectId),
        date: new Date(trackingDocumentForm.get('date').value).setHours(
          new Date(trackingDocumentForm.get('date').value).getHours() - new Date(trackingDocumentForm.get('date').value).getTimezoneOffset() / 60
        ),
        providedService: trackingDocumentForm.get('providedService').value,
        activity: trackingDocumentForm.get('activity').value,
        description: trackingDocumentForm.get('note').value,
        dateOfModification: new Date(trackingDocumentForm.get('dateOfModification').value).setHours(
          new Date(trackingDocumentForm.get('dateOfModification').value).getHours() - new Date(trackingDocumentForm.get('dateOfModification').value).getTimezoneOffset() / 60
        ),
      };
      this.trackingListService.addServiceProviderItem(this.item).subscribe(
        (res: any) => {
          this.dialogRef.close({ event: 'SUCCESS' });
        },
        (err: string) => {
          console.log('Error occured' + err);
        }
      );
    }
  }
  
  closeDialog() {
    this.dialogRef.close({ event: ModalEventEnum.CANCEL });
  }
  displayCustomType(objectValue: any) {
    return objectValue ? objectValue.title : '';
  }
  public _filter(value: string, type: string): any[] {
    const filterValue = value.toLowerCase();

    switch (type) {
      case AutocompleteFilterEnum.ACTIVITY:
        return sortByFirstLetter(this.activities.filter(option => option.title.toLowerCase().includes(filterValue)),
          filterValue,
          'title'
        );
      case AutocompleteFilterEnum.PROVIDED_SERVICE:
        return sortByFirstLetter(this.providedServices.filter(option => option.title.toLowerCase().includes(filterValue)),
          filterValue,
          'title'
        );
    }
  }

  async autoSelectType() {
    autoSelect(this.activities, 'activity', 'title', this.trackingDocumentForm);
    await new Promise(resolve => setTimeout(resolve, 600));

    if (this.trackingDocumentForm.get('activity').value?.id === undefined) {
      this.isActivitySelected = false;
    } else {
      this.isActivitySelected = true;
    }
  }

  onInputChange(event: any) {
    this.isActivitySelected = false;
  }

  onProvidedServiceSelected(event: any) {
    const providedService = event.option.value;

    this.codebookService.getProvidedServiceActivityCodebook(providedService.id).subscribe(result => {
      this.trackingDocumentForm.get('activity').reset();
      this.activities = result;
      this.filteredActivities = this.trackingDocumentForm.controls.activity.valueChanges.pipe(
        startWith(''),
        map(value => (typeof value === 'string' ? value : value?.title)),
        map(title => (title ? this._filter(title, AutocompleteFilterEnum.ACTIVITY) : this.activities.slice()))
      )
    });
  }

  onActivitySelected(event: any) {
    this.isActivitySelected = true;
  }

}
